import { Document } from '@react-pdf/renderer';

import { CostApproachPage } from './pages/CostApproachPage.tsx';
import { CoverPage } from './pages/CoverPage.tsx';
import { DateOfSaleAdjustmentsPage } from './pages/DateOfSaleAdjustmentsPage.tsx';
import { DepreciatedCostAdjustmentsPage } from './pages/DepreciatedCostAdjustmentsPage.tsx';
import { ListToSalePage } from './pages/ListToSalePage.tsx';
import { MarketConditionPage } from './pages/MarketConditionPage.tsx';
import { MethodologyPage } from './pages/MethodologyPage.tsx';
import { PriorSalesPage } from './pages/PriorSalesPage.tsx';
import { PropertyOrderDetailsPage } from './pages/PropertyOrderDetailsPage.tsx';
import { ReconciliationPage } from './pages/ReconciliationPage.tsx';
import { SalesComparablePhotosPage } from './pages/SalesComparablePhotosPage.tsx';
import { SalesComparisonMapPage } from './pages/SalesComparisonMapPage.tsx';
import { SalesComparisonPage } from './pages/SalesComparisonPage.tsx';
import { SiteExtractionAdjustmentsPage } from './pages/SiteExtractionAdjustmentsPage.tsx';
import { CertificationSignaturePage } from './pages/certification/CertificationSignaturePage.tsx';
import { CertificationStatementPage } from './pages/certification/CertificationStatementPage.tsx';
import { Tkv2PDF, Tkv2PDFChartImages, Tkv2SignatureImages } from './types.ts';
import { UadDefinitionsPage } from './pages/UadDefinitionsPage.tsx';

export const Tkv2PDFModule = ({
  subjectAddress,
  opinionOfMarketValue,
  totalCostToCure,
  propertyImage,
  clientName,
  clientAddress,
  appraiserFee,
  amcFee,
  effectiveDate,
  propertyType,
  shortPropertyType,
  inspectorType,
  orderNumber,
  intendedUsers,
  intendedUse,
  borrowerName,
  inspectionDate,
  zoning,
  zoningCompliance,
  parcelNumber,
  propertyInterest,
  hoa,
  hoaFees,
  legalDescription,
  propertyMapImage,
  appraisalType,
  mapBoxCoordinates,
  mapStatistics,
  subjectSalesComparisonData,
  compsForMaps,
  subjectCoordinates,
  comparablesSalesComparisonData,
  adjustmentSettings,
  manualAdjustmentsEntities,
  commentaryV2,
  adjustmentsTableData,
  hasFunctionalDepreciation,
  hasExternalDepreciation,
  tkv2ChartImages,
  licenseExpiration,
  licenseNumber,
  licenseState,
  licenseType,
  signedAt,
  appraiserName,
  signatureImages,
  compSequence,
  priorSales,
  inspectionData,
  includeTotalCostToCure,
  totalRenovationBudget,
  opinionOfAfterRepairValue,
  estimatedROI,
  costApproachData,
  reconciliationPageData,
  isRevision,
  revisionType,
  alerts,
  marketAggregateData,
  countyName,
  countyFIPS,
  revisionData,
  salesComparablePhotosData,
  hasArvOption,
  compTypes,
  subjectArvSalesComparisonData,
  arvAdjustmentsTableData,
}: Tkv2PDF & { tkv2ChartImages: Tkv2PDFChartImages; signatureImages: Tkv2SignatureImages }) => (
  <Document>
    <CoverPage
      subjectAddress={subjectAddress}
      opinionOfMarketValue={opinionOfMarketValue}
      totalCostToCure={totalCostToCure}
      propertyImage={propertyImage ?? ''}
      intendedUsers={intendedUsers}
      effectiveDate={effectiveDate}
      shortPropertyType={shortPropertyType}
      inspectorType={inspectorType}
      appraisalType={appraisalType}
      includeTotalCostToCure={includeTotalCostToCure}
      hasArvOption={hasArvOption}
      totalRenovationBudget={totalRenovationBudget}
      opinionOfAfterRepairValue={opinionOfAfterRepairValue}
      estimatedROI={estimatedROI}
      clientName={clientName}
    />
    <PropertyOrderDetailsPage
      subjectAddress={subjectAddress}
      shortPropertyType={shortPropertyType}
      appraisalType={appraisalType}
      effectiveDate={effectiveDate}
      inspectionDate={inspectionDate}
      clientName={clientName}
      clientAddress={clientAddress}
      borrowerName={borrowerName}
      zoning={zoning}
      zoningCompliance={zoningCompliance}
      parcelNumber={parcelNumber}
      propertyInterest={propertyInterest}
      hoa={hoa}
      hoaFees={hoaFees}
      legalDescription={legalDescription}
      orderNumber={orderNumber}
      intendedUsers={intendedUsers}
      intendedUse={intendedUse}
      propertyMapImage={propertyMapImage}
      hasArvOption={hasArvOption}
      subjectSalesComparisonData={subjectSalesComparisonData}
    />
    <SalesComparisonPage
      subjectAddress={subjectAddress}
      subjectSalesComparisonData={subjectSalesComparisonData}
      subjectArvSalesComparisonData={subjectArvSalesComparisonData}
      comparables={Object.values(comparablesSalesComparisonData)}
      hasArvOption={hasArvOption}
    />
    {subjectSalesComparisonData && comparablesSalesComparisonData && (
      <SalesComparisonMapPage
        subjectAddress={subjectAddress}
        subjectLatitude={subjectCoordinates?.latitude}
        subjectLongitude={subjectCoordinates?.longitude}
        comps={compsForMaps}
        mapBoxCoordinates={mapBoxCoordinates}
        statistics={mapStatistics}
        compSequence={compSequence}
        compTypes={compTypes}
        hasArvOption={hasArvOption}
      />
    )}
    <DateOfSaleAdjustmentsPage
      subjectAddress={subjectAddress}
      adjustmentSettings={adjustmentSettings}
      adjustmentsTableData={adjustmentsTableData}
      tkv2ChartImages={tkv2ChartImages}
      compSequence={compSequence}
      compTypes={compTypes}
    />
    <ListToSalePage
      subjectAddress={subjectAddress}
      adjustmentSettings={adjustmentSettings}
      adjustmentsTableData={adjustmentsTableData}
      compSequence={compSequence}
      compTypes={compTypes}
    />
    <SiteExtractionAdjustmentsPage
      subjectAddress={subjectAddress}
      adjustmentSettings={adjustmentSettings}
      manualAdjustmentsEntities={manualAdjustmentsEntities}
      commentaryV2={commentaryV2}
      adjustmentsTableData={adjustmentsTableData}
      compSequence={compSequence}
      compTypes={compTypes}
      tkv2ChartImages={tkv2ChartImages}
    />
    <DepreciatedCostAdjustmentsPage
      subjectAddress={subjectAddress}
      commentaryV2={commentaryV2}
      adjustmentsTableData={adjustmentsTableData}
      arvAdjustmentsTableData={arvAdjustmentsTableData}
      hasArvOption={hasArvOption}
      hasFunctionalDepreciation={hasFunctionalDepreciation}
      hasExternalDepreciation={hasExternalDepreciation}
    />
    <CostApproachPage subjectAddress={subjectAddress} costApproachData={costApproachData} />
    <MarketConditionPage
      subjectAddress={subjectAddress}
      commentaryV2={commentaryV2}
      marketAggregateData={marketAggregateData}
      countyName={countyName}
      countyFIPS={countyFIPS}
    />
    <PriorSalesPage
      subjectAddress={subjectAddress}
      commentaryV2={commentaryV2}
      compSequence={compSequence}
      priorSales={priorSales}
      compTypes={compTypes}
    />
    <MethodologyPage
      subjectAddress={subjectAddress}
      propertyType={propertyType}
      appraiserFee={appraiserFee}
      amcFee={amcFee}
      hasArvOption={hasArvOption}
    />
    <UadDefinitionsPage subjectAddress={subjectAddress} />
    <ReconciliationPage
      subjectAddress={subjectAddress}
      isRevision={isRevision}
      revisionType={revisionType}
      commentaryV2={commentaryV2}
      alerts={alerts}
      revisionData={revisionData}
      {...reconciliationPageData}
    />
    <CertificationStatementPage subjectAddress={subjectAddress} />
    <CertificationSignaturePage
      subjectAddress={subjectAddress}
      commentaryV2={commentaryV2}
      effectiveDate={effectiveDate}
      inspectionDate={inspectionDate}
      licenseNumber={licenseNumber}
      licenseState={licenseState}
      licenseExpiration={licenseExpiration}
      licenseType={licenseType}
      signedAt={signedAt}
      appraiserName={appraiserName}
      signatureImages={signatureImages}
      inspectionData={inspectionData}
    />
    <SalesComparablePhotosPage
      subjectAddress={subjectAddress}
      salesComparablePhotosData={salesComparablePhotosData}
      compSequence={compSequence}
    />
  </Document>
);
